/**
 * SCORM API client — HTTP calls to the SCORM runtime endpoints.
 *
 * All runtime calls (commit, restore) use the Session JWT.
 * Upload calls use the tenant JWT / API token via sharedApiClient.
 */

import { sharedApiClient, getApiConfig } from '@ai-content-hub/shared-api';

import type {
    ScormLaunchConfig,
    ScormCommitResult,
    ScormRuntimeState,
    ScormUploadResult,
} from './types';

// ── Runtime API (Session JWT auth) ──────────────────────────────────────────

/** Resolve the Content API base URL from shared config. */
function getBaseUrl(): string {
    try {
        return getApiConfig().baseUrl;
    } catch {
        // Fallback when shared API is not configured (e.g. standalone editor)
        return '/api';
    }
}

/**
 * Commit CMI data to the backend.
 * POST /api/content-sessions/{sessionId}/scorm/commit
 */
export async function commitCmi(
    sessionId: string,
    sessionToken: string,
    scoId: string,
    cmiData: Record<string, unknown>,
): Promise<ScormCommitResult> {
    const base = getBaseUrl();
    const res = await fetch(`${base}/content-sessions/${sessionId}/scorm/commit`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Session ${sessionToken}`,
        },
        body: JSON.stringify({ sco_id: scoId, cmi_data: cmiData }),
    });

    const json = await res.json();
    if (!res.ok || json.success === false) {
        throw new Error(json.message || 'CMI commit failed');
    }
    return json.data as ScormCommitResult;
}

/**
 * Restore CMI data for session resume.
 * GET /api/content-sessions/{sessionId}/scorm/runtime?sco_id=...
 */
export async function restoreCmi(
    sessionId: string,
    sessionToken: string,
    scoId: string = 'default',
): Promise<ScormRuntimeState> {
    const base = getBaseUrl();
    const qs = new URLSearchParams({ sco_id: scoId }).toString();
    const res = await fetch(
        `${base}/content-sessions/${sessionId}/scorm/runtime?${qs}`,
        {
            method: 'GET',
            headers: {
                'Authorization': `Session ${sessionToken}`,
            },
        },
    );

    const json = await res.json();
    if (!res.ok || json.success === false) {
        throw new Error(json.message || 'CMI restore failed');
    }
    return json.data as ScormRuntimeState;
}

/**
 * Get SCORM launch configuration for a session.
 * GET /api/content-sessions/{sessionId}/scorm/launch
 */
export async function getScormLaunchConfig(
    sessionId: string,
    sessionToken: string,
): Promise<ScormLaunchConfig> {
    const base = getBaseUrl();
    const res = await fetch(
        `${base}/content-sessions/${sessionId}/scorm/launch`,
        {
            method: 'GET',
            headers: {
                'Authorization': `Session ${sessionToken}`,
            },
        },
    );

    const json = await res.json();
    if (!res.ok || json.success === false) {
        throw new Error(json.message || 'SCORM launch config failed');
    }
    return json.data as ScormLaunchConfig;
}

/**
 * Call the SCORM QCO API (apidb endpoint).
 * POST /api/content-sessions/{sessionId}/scorm/apidb
 *
 * Used by the API_DB proxy to forward createQcoInstance / putIfAbsent / confirm
 * calls from the SCO to the backend scoring engine.
 */
export async function callApiDb(
    sessionId: string,
    sessionToken: string,
    action: string,
    payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
    const base = getBaseUrl();
    const res = await fetch(`${base}/content-sessions/${sessionId}/scorm/apidb`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Session ${sessionToken}`,
        },
        body: JSON.stringify({ action, ...payload }),
    });

    const json = await res.json();
    if (!res.ok || json.success === false) {
        throw new Error(json.message || `apidb ${action} failed`);
    }
    return json.data as Record<string, unknown>;
}

// ── Package Management API (Tenant JWT / API Token auth) ────────────────────

/**
 * Upload a SCORM zip package.
 * POST /api/content/scorm/upload
 */
export async function uploadScormPackage(
    file: File,
    contentId?: string,
): Promise<ScormUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (contentId) {
        formData.append('content_id', contentId);
    }

    const res = await sharedApiClient.postFormData<ScormUploadResult>(
        '/content/scorm/upload',
        formData,
    );

    return res.data;
}

/**
 * Get SCORM package launch info (for editor preview).
 * GET /api/content/scorm/{contentId}/launch
 */
export async function getScormPackageInfo(
    contentId: string,
): Promise<ScormUploadResult | null> {
    const res = await sharedApiClient.get<ScormUploadResult>(
        `/content/scorm/${contentId}/launch`,
    );

    if (!res.ok || !res.data) {
        return null;
    }
    return res.data;
}

/**
 * Delete a SCORM package.
 * DELETE /api/content/scorm/{contentId}/package
 */
export async function deleteScormPackage(
    contentId: string,
): Promise<boolean> {
    const res = await sharedApiClient.delete(`/content/scorm/${contentId}/package`);
    return res.ok;
}

/** Result of a SCORM access grant (EAA-1644 WP4 contract). */
export interface ScormAccessGrant {
    /** Package root: relative server path (`/api/content/scorm/{id}/files/`) or absolute CDN URL. */
    packageBaseUrl: string;
    /** ISO-8601 UTC instant after which the CDN cookies (if any) stop working. */
    expiresAt: string;
}

/** Why an access request failed — callers branch on `kind`, never on the message. */
export type ScormAccessErrorKind = 'not_found' | 'unauthorized' | 'forbidden' | 'unavailable' | 'network' | 'unknown';

export class ScormAccessError extends Error {
    readonly kind: ScormAccessErrorKind;
    readonly status: number;
    readonly code: number | null;

    constructor(kind: ScormAccessErrorKind, status: number, code: number | null, message: string) {
        super(message);
        this.name = 'ScormAccessError';
        this.kind = kind;
        this.status = status;
        this.code = code;
    }
}

export interface RequestScormAccessParams {
    /** Package id — used for the tenant variant when no session is available. */
    packageId?: string | null;
    /** Content-session id — when present the session variant is called. */
    sessionId?: string | null;
    /** API base including the `/api` prefix (e.g. `https://api.example.com/api`). Defaults to shared config. */
    apiBaseUrl?: string;
    /** Auth headers to send (tenant headers, or `Authorization: Session <jwt>`). */
    authHeaders?: Record<string, string>;
}

function accessErrorKind(status: number): ScormAccessErrorKind {
    if (status === 404) return 'not_found';
    if (status === 401) return 'unauthorized';
    if (status === 403) return 'forbidden';
    if (status === 503) return 'unavailable';
    return 'unknown';
}

/**
 * EAA-1644 WP5: ask the backend for the SCORM package base URL (and, in CDN delivery
 * mode, the CloudFront signed cookies set on the response).
 *
 * POST {api}/content-sessions/{sessionId}/scorm/access when a session is known,
 * otherwise POST {api}/content/scorm/{packageId}/access. `credentials: 'include'` is
 * required: without it the browser discards the Set-Cookie of a cross-origin response.
 */
export async function requestScormAccess({
    packageId,
    sessionId,
    apiBaseUrl,
    authHeaders,
}: RequestScormAccessParams): Promise<ScormAccessGrant> {
    const base = (apiBaseUrl || getBaseUrl()).replace(/\/+$/, '');
    let path: string;
    if (sessionId) {
        path = `/content-sessions/${encodeURIComponent(sessionId)}/scorm/access`;
    } else if (packageId) {
        path = `/content/scorm/${encodeURIComponent(packageId)}/access`;
    } else {
        throw new ScormAccessError('not_found', 0, null, 'No SCORM package or session to request access for');
    }

    let res: Response;
    try {
        res = await fetch(`${base}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: { ...(authHeaders ?? {}) },
        });
    } catch (err) {
        throw new ScormAccessError('network', 0, null, err instanceof Error ? err.message : 'SCORM access request failed');
    }

    let json: { success?: boolean; code?: number; message?: string; data?: Record<string, unknown> } | null = null;
    try {
        json = await res.json();
    } catch {
        json = null;
    }

    if (!res.ok || !json || json.success === false) {
        const code = typeof json?.code === 'number' ? json.code : null;
        throw new ScormAccessError(accessErrorKind(res.status), res.status, code, json?.message || `SCORM access failed (HTTP ${res.status})`);
    }

    const packageBaseUrl = json.data?.package_base_url;
    const expiresAt = json.data?.expires_at;
    if (typeof packageBaseUrl !== 'string' || !packageBaseUrl || typeof expiresAt !== 'string') {
        throw new ScormAccessError('unknown', res.status, null, 'SCORM access response is missing package_base_url');
    }
    return { packageBaseUrl, expiresAt };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Package id for the access call: `data.packageId` when it is a real id, else parsed from
 * a stored `/scorm/{id}/files/` URL. Preview uploads (`preview_…`) have no saved package,
 * so they yield `null` and the caller keeps the stored URL.
 */
export function resolveScormPackageId(packageId: unknown, packageUrl: unknown): string | null {
    if (typeof packageId === 'string' && UUID_RE.test(packageId)) {
        return packageId;
    }
    if (typeof packageUrl !== 'string') return null;
    const match = /\/scorm\/([^/?#]+)\/files(?:\/|$)/.exec(packageUrl);
    if (match && UUID_RE.test(match[1])) {
        return match[1];
    }
    return null;
}

/**
 * Resolve a relative URL to absolute using the API base URL.
 */
export function getAbsoluteUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
        return url;
    }
    try {
        const baseUrl = getApiConfig().baseUrl;
        if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://') && !baseUrl.startsWith('//')) {
            return url;
        }
        const origin = new URL(baseUrl).origin;
        const cleanOrigin = origin.replace(/\/+$/, '');
        const cleanUrl = url.replace(/^\/+/, '');
        return `${cleanOrigin}/${cleanUrl}`;
    } catch {
        return url;
    }
}
